import { useNavigate, useParams } from "react-router";
import { useBuildLink } from "src/hooks/useBuildLink";
import { useQueryTask } from "src/queries/tasks/tasks.query";
import type { CommonParams } from "src/queries/useCommonQueryParams";
import type {
  ComputeRedirectByNameAndPitPath,
  PathParams,
  PitPath,
  TaskRedirectByNameAndPitPath,
} from "src/utils/appRoutes/appRoutes.types";
import { newTab as newTabFn } from "src/utils/newTab";

const getCommonPathParams = (urlParams: Partial<CommonParams>) => ({
  app_name: urlParams.app_name,
  env_name: urlParams.env_name,
  redirect: true,
});

export const useChangePit = () => {
  const buildLink = useBuildLink();
  const navigate = useNavigate();
  const urlParams = useParams();
  const { data: currentTask } = useQueryTask();

  return ({
    pit: app_pit,
    newTab = false,
    viewTransition,
  }: {
    pit: string;
    newTab?: boolean;
    viewTransition?: boolean;
  }) => {
    if (!urlParams.app_name) {
      throw new Error("app_name is required to use useChangePit");
    }
    const search = window.location.search;

    // Default: navigate to the pit on the pipeline page (pitPath)
    let pathParams: PathParams = {
      app_pit,
      app_name: urlParams.app_name,
    } as PitPath;

    if (urlParams.cluster_name) {
      // On a compute page: redirect to the same compute at the new pit (computeRedirectByNameAndPitPath)
      pathParams = {
        ...getCommonPathParams(urlParams),
        run_time: app_pit,
        cluster_name: urlParams.cluster_name,
      } as ComputeRedirectByNameAndPitPath;
    } else if (urlParams.task_name) {
      // On a task page: redirect to the same task at the new pit (taskRedirectByNameAndPitPath)
      pathParams = {
        ...getCommonPathParams(urlParams),
        app_pit,
        task_name: urlParams.task_name,
      } as TaskRedirectByNameAndPitPath;
    } else if (currentTask) {
      // On a task-run page: redirect to the task at the new pit (taskRedirectByNameAndPitPath)
      pathParams = {
        ...getCommonPathParams(urlParams),
        app_pit,
        task_name: currentTask.task_name,
      } as TaskRedirectByNameAndPitPath;
    }

    const link = buildLink({ ...pathParams, search }) + window.location.hash;

    if (link) {
      if (newTab) {
        newTabFn(link);
      } else {
        void navigate(link, viewTransition ? { viewTransition: true } : undefined);
      }
    } else {
      console.error("Failed building link for pit", app_pit);
    }
  };
};
